import { useCallback } from "react";

import { usePersistedState } from "./use-persisted-state";
import { RECENTS_SECTION_ROWS } from "#app/lib/constants/recents";

export const RECENT_ENTITY_LIMIT = RECENTS_SECTION_ROWS;

/**
 * The viewer's recently-opened ids for one entity kind, newest first, mirrored
 * in localStorage under `key` (a workspace-scoped key, so switching workspaces
 * reads that workspace's list).
 *
 * Ids only: the kinds that use this (Accounts, Contacts) live in a
 * workspace-wide store the section resolves them against at render, so a
 * deleted row simply drops out and a rename reaches the menu at once — a
 * display snapshot would only go stale. Every hook on the key shares one
 * state (`usePersistedState`), so the provider recording a visit and the
 * menu reading the list see the same rows in the same render.
 */
export function useRecentEntityIds(key: string) {
  const { state, update } = usePersistedState(key, {
    ids: [] as string[],
  });

  const recordVisit = useCallback(
    (id: string) => {
      update((current) => ({
        ids: [id, ...current.ids.filter((other) => other !== id)].slice(
          0,
          RECENT_ENTITY_LIMIT,
        ),
      }));
    },
    [update],
  );

  return { ids: state.ids, recordVisit };
}
